// ABOUTME: React component for managing family goals with multi-child selection, creation, editing, and progress tracking
// ABOUTME: Features Material-UI button-based child selection, toast notifications, and comprehensive goal management interface

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSignalR } from '../contexts/SignalRContext';
import { Child, Goal, GoalType, GoalStatus, CreateGoalRequest, GoalStatusCounts } from '../types/api';
import { goalsAPI } from '../services/api';
import { Target, Plus, Trash2, Edit, Camera, Copy, Repeat, PartyPopper } from 'lucide-react';
import { Shield as ShieldIcon } from '@mui/icons-material';
import AvatarUploadModal from './AvatarUploadModal';
import CelebrationModal from './CelebrationModal';
import { getFullApiUrl } from '../utils/routeUtils';
import { isGoalEndingToday } from '../utils/dateUtils';
import {
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Fade,
  Avatar,
  ButtonGroup,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TablePagination
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { celebrationGold, celebrationGoldHover, neutralGray } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';

interface GoalsManagerProps {
  children: Child[];
  familyId: number;
  familyTimezone?: string;
}

const GoalsManager: React.FC<GoalsManagerProps> = ({ children, familyId, familyTimezone }) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [searchParams] = useSearchParams();
  const { on, connectionState } = useSignalR();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateGoal, setShowCreateGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showRemoveImageConfirmation, setShowRemoveImageConfirmation] = useState(false);
  const [celebrationError, setCelebrationError] = useState<string | null>(null);
  const [celebrationModal, setCelebrationModal] = useState<{
    isOpen: boolean;
    goal: Goal | null;
    childName: string;
  }>({ isOpen: false, goal: null, childName: '' });

  // Initialize goalFilter from URL parameter if present
  const initialFilter = searchParams.get('filter');
  const [goalFilter, setGoalFilter] = useState<'open' | 'completed' | 'celebrated' | 'failed'>(
    (initialFilter === 'completed' || initialFilter === 'celebrated' || initialFilter === 'failed') ? initialFilter : 'open'
  );
  const [selectedChildIds, setSelectedChildIds] = useState<number[]>([]);

  // Enhanced filtering state (Issue #883)
  // When scoped to a single child (e.g., GoalsTab on ChildProfilePage), pre-filter to that child
  const [childFilter, setChildFilter] = useState<number | null>(
    children.length === 1 ? (children[0].childId ?? null) : null
  );
  const [goalTypeFilters, setGoalTypeFilters] = useState<GoalType[]>([
    GoalType.HeartStreak,
    GoalType.StarCollection,
    GoalType.ResponsibilitiesCompleted,
    GoalType.HeartCollection
  ]); // All checked by default
  const [recurringFilter, setRecurringFilter] = useState<boolean>(false); // false = show all, true = recurring only

  // Server-side pagination state (Issue #1426)
  const [page, setPage] = useState(0); // 0-based for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<GoalStatusCounts>({ open: 0, completed: 0, celebrated: 0, failed: 0 });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Delete confirmation modal state (Issue #885)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<Goal | null>(null);
  const [newGoal, setNewGoal] = useState<CreateGoalRequest>({
    childId: 0,
    title: '',
    type: GoalType.HeartStreak,
    targetValue: 5, // Matches heartStreakTemplate.consecutiveDays
    reward: '',
    dayPasses: 1, // Issue #486: Default day passes
    isRecurring: false, // Issue #431: Recurring goals flag
    isNukeExempt: false, // Issue #1310: Exempt from nuclear reset
    heartStreakTemplate: {
      consecutiveDays: 5,
      dayPasses: 1
    },
    starCollectionTemplate: {
      totalStars: 10,
      timePeriodDays: 0  // Default to unlimited time (no limit)
    },
    heartCollectionTemplate: {
      totalHearts: 10,
      timePeriodDays: 0  // Default to unlimited time (no limit)
    },
    responsibilitiesCompletedTemplate: {
      totalLists: 5,
      timePeriodDays: 0  // Default to unlimited time (no limit)
    }
  });

  // Server-side paginated goal loading (Issue #1426)
  const loadGoals = async (overridePage?: number) => {
    try {
      setIsLoading(true);
      const currentPage = overridePage !== undefined ? overridePage : page;
      const response = await goalsAPI.getFamilyGoalsPaginated(familyId, {
        page: currentPage + 1, // API uses 1-based pages
        pageSize: rowsPerPage,
        filterStatus: goalFilter,
        childId: childFilter,
        goalTypes: goalTypeFilters.length < 3 ? goalTypeFilters : undefined, // Only send if not all selected
        isRecurring: recurringFilter ? true : undefined, // Only send when filtering for recurring
      });
      setGoals(response.data);
      setTotalCount(response.totalCount);
      setStatusCounts(response.statusCounts);
    } catch (error) {
      console.error('Failed to load goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadGoals intentionally omitted; it is stable and adding it would cause extra fetches
  }, [familyId, page, rowsPerPage]);

  // Reset to page 0 and reload when filters change (skip initial mount — handled by [familyId, page, rowsPerPage] effect)
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true;
      return;
    }
    setPage(0);
    loadGoals(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadGoals intentionally omitted; it is stable and adding it would cause extra fetches
  }, [goalFilter, childFilter, goalTypeFilters, recurringFilter]);

  // Skip filter effect on initial mount — initial load is handled by [familyId, page, rowsPerPage] effect
  const filterMountedRef = useRef(false);
  // Ref so reconnect watcher always calls the latest loadGoals without stale closure
  const loadGoalsRef = useRef(() => loadGoals());
  loadGoalsRef.current = () => loadGoals();
  // Track previous connectionState to detect Disconnected→Connected transitions for re-fetch
  const prevConnectionStateRef = useRef<string>(connectionState);

  // Subscribe to real-time goal events and reload the list
  useEffect(() => {
    const unsubUpdated = on('GoalUpdated', () => loadGoals());
    const unsubDeleted = on('GoalDeleted', () => loadGoals());
    const unsubPeriodReset = on('PeriodReset', () => loadGoalsRef.current());
    return () => {
      unsubUpdated();
      unsubDeleted();
      unsubPeriodReset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // Re-fetch goals when SignalR reconnects (Disconnected→Connected transition).
  useEffect(() => {
    if (prevConnectionStateRef.current !== 'Connected' && connectionState === 'Connected') {
      loadGoalsRef.current();
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState]);

  const resetForm = () => {
    setSelectedChildIds([]);
    setNewGoal({
      childId: 0,
      title: '',
      type: GoalType.HeartStreak,
      targetValue: 5, // Matches heartStreakTemplate.consecutiveDays
      reward: '',
      rewardImageUrl: undefined,
      dayPasses: 0,
      isRecurring: false, // Issue #431: Reset recurring flag
      isNukeExempt: false, // Issue #1310: Reset exempt flag
      heartStreakTemplate: {
        consecutiveDays: 5,
        dayPasses: 0
      },
      starCollectionTemplate: {
        totalStars: 10,
        timePeriodDays: 0
      },
      heartCollectionTemplate: {
        totalHearts: 10,
        timePeriodDays: 0
      },
      responsibilitiesCompletedTemplate: {
        totalLists: 5,
        timePeriodDays: 0
      }
    });
    setEditingGoal(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const parseHeartStreakFromTitle = (title: string) => {
    // Parse "Get hearts 5 days in a row (1 day passes)" or legacy "squares allowed"
    const daysMatch = title.match(/Get hearts (\d+) days/);
    const squaresMatch = title.match(/\((\d+) (?:day passes|squares allowed)\)/);
    return {
      consecutiveDays: daysMatch ? parseInt(daysMatch[1]) : 5,
      dayPasses: squaresMatch ? parseInt(squaresMatch[1]) : 0
    };
  };

  const parseStarCollectionFromTitle = (title: string) => {
    // Parse both "Collect 10 stars in 7 days" and "Collect 10 stars" (Issue #488: No more squares)
    const starsMatch = title.match(/Collect (\d+) stars/);
    const daysMatch = title.match(/in (\d+) days/);
    return {
      totalStars: starsMatch ? parseInt(starsMatch[1]) : 10,
      // Issue #487: If no "in X days" found, it means unlimited time (0)
      timePeriodDays: daysMatch ? parseInt(daysMatch[1]) : 0
    };
  };

  const parseHeartCollectionFromTitle = (title: string) => {
    // Parse both "Collect 10 hearts in 30 days" and "Collect 10 hearts"
    const heartsMatch = title.match(/Collect (\d+) hearts/);
    const daysMatch = title.match(/in (\d+) days/);
    return {
      totalHearts: heartsMatch ? parseInt(heartsMatch[1]) : 10,
      // Issue #487: If no "in X days" found, it means unlimited time (0)
      timePeriodDays: daysMatch ? parseInt(daysMatch[1]) : 0
    };
  };

  const parseResponsibilitiesCompletedFromTitle = (title: string) => {
    // Parse both "Complete 5 responsibility lists in 30 days" and "Complete 5 responsibility lists"
    const listsMatch = title.match(/Complete (\d+) responsibility lists/);
    const daysMatch = title.match(/in (\d+) days/);
    return {
      totalLists: listsMatch ? parseInt(listsMatch[1]) : 5,
      timePeriodDays: daysMatch ? parseInt(daysMatch[1]) : 0
    };
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setSelectedChildIds([goal.childId]); // Set the selected child for editing
    
    // Parse template data from the auto-generated title
    const heartStreakTemplate = goal.type === GoalType.HeartStreak 
      ? parseHeartStreakFromTitle(goal.title)
      : { consecutiveDays: 5, dayPasses: 1 };
    
    const starCollectionTemplate = goal.type === GoalType.StarCollection
      ? parseStarCollectionFromTitle(goal.title)
      : { totalStars: 10, timePeriodDays: 7 };
    
    const heartCollectionTemplate = goal.type === GoalType.HeartCollection
      ? parseHeartCollectionFromTitle(goal.title)
      : { totalHearts: 10, timePeriodDays: 30 };

    const responsibilitiesCompletedTemplate = goal.type === GoalType.ResponsibilitiesCompleted
      ? parseResponsibilitiesCompletedFromTitle(goal.title)
      : { totalLists: 5, timePeriodDays: 0 };

    setNewGoal({
      childId: goal.childId,
      title: goal.title,
      type: goal.type,
      targetValue: goal.targetValue,
      reward: goal.rewardDescription || goal.reward || '',
      rewardImageUrl: goal.rewardImageUrl,
      dayPasses: goal.type === GoalType.StarCollection || goal.type === GoalType.ResponsibilitiesCompleted || goal.type === GoalType.HeartCollection ? 0 : (goal.dayPasses ?? 0),  // Issue #488: Collection goals don't use squares
      isRecurring: goal.isRecurring || false, // Issue #431: Include recurring flag
      isNukeExempt: goal.isNukeExempt || false, // Issue #1310: Preserve exempt flag when editing
      heartStreakTemplate,
      starCollectionTemplate,
      heartCollectionTemplate,
      responsibilitiesCompletedTemplate
    });
    setShowCreateGoal(true);
  };

  const handleImageUpload = async (imageUrl: string | null) => {
    if (imageUrl && editingGoal && editingGoal.rewardImageUrl) {
      // For existing goals that already have an image, upload the replacement image immediately
      try {
        await goalsAPI.uploadGoalImage(editingGoal.id, imageUrl);
        // Reload the goal to get the updated image URL
        await loadGoals();
        // Update the form state with a placeholder - the real URL will come from loadGoals
        setNewGoal({ ...newGoal, rewardImageUrl: 'uploading...' });
        // Give a moment for the state to update, then set the real URL
        setTimeout(async () => {
          const updatedGoal = await goalsAPI.getGoal(editingGoal.id);
          if (updatedGoal?.rewardImageUrl) {
            setNewGoal(prev => ({ ...prev, rewardImageUrl: updatedGoal.rewardImageUrl }));
          }
        }, 500);
      } catch (error) {
        console.error('Failed to upload goal image:', error);
        alert('Failed to upload image. Please try again.');
        return;
      }
    } else {
      // For new goals OR existing goals without images, store the image data for upload during save
      setNewGoal({ ...newGoal, rewardImageUrl: imageUrl || undefined });
    }
    setShowImageUpload(false);
  };

  const handleRemoveImage = () => {
    setNewGoal({ ...newGoal, rewardImageUrl: undefined });
    setShowRemoveImageConfirmation(false);
  };

  const handleSaveGoal = async () => {
    if (selectedChildIds.length === 0 && !editingGoal) {
      showSnackbar('Please select at least one child', 'error');
      return;
    }
    
    if (editingGoal && !newGoal.childId) {
      showSnackbar('Please select a child', 'error');
      return;
    }

    // Auto-generate title based on goal type and template
    let generatedTitle = '';
    if (newGoal.type === GoalType.HeartStreak) {
      const days = newGoal.heartStreakTemplate?.consecutiveDays || 5;
      const squares = newGoal.dayPasses ?? 0;
      if (squares > 0) {
        const passText = squares === 1 ? 'day pass' : 'day passes';
        generatedTitle = `Get hearts ${days} days in a row (${squares} ${passText})`;
      } else {
        generatedTitle = `Get hearts ${days} days in a row`;
      }
    } else if (newGoal.type === GoalType.StarCollection) {
      const stars = newGoal.starCollectionTemplate?.totalStars || 10;
      const days = newGoal.starCollectionTemplate?.timePeriodDays || 0;
      
      // Issue #487: Only include time period if days > 0 (unlimited time is the default)
      // Issue #488: Remove squares from collection goal titles
      if (days > 0) {
        generatedTitle = `Collect ${stars} stars in ${days} days`;
      } else {
        generatedTitle = `Collect ${stars} stars`;
      }
    } else if (newGoal.type === GoalType.ResponsibilitiesCompleted) {
      const lists = newGoal.responsibilitiesCompletedTemplate?.totalLists || 5;
      const days = newGoal.responsibilitiesCompletedTemplate?.timePeriodDays || 0;
      if (days > 0) {
        generatedTitle = `Complete ${lists} responsibility lists in ${days} days`;
      } else {
        generatedTitle = `Complete ${lists} responsibility lists`;
      }
    } else if (newGoal.type === GoalType.HeartCollection) {
      const hearts = newGoal.heartCollectionTemplate?.totalHearts || 10;
      const days = newGoal.heartCollectionTemplate?.timePeriodDays || 0;

      // Issue #487: Only include time period if days > 0 (unlimited time is the default)
      // Issue #488: Remove squares from collection goal titles
      if (days > 0) {
        generatedTitle = `Collect ${hearts} hearts in ${days} days`;
      } else {
        generatedTitle = `Collect ${hearts} hearts`;
      }
    }

    try {
      if (editingGoal) {
        // Issue #1613: Send durationDays only — server recomputes EndDate using family timezone.
        // Avoids UTC-midnight off-by-one for negative-UTC-offset families (Bug C fix).
        let updatedDurationDays: number | null = null;
        if (newGoal.type === GoalType.StarCollection) {
          const days = newGoal.starCollectionTemplate?.timePeriodDays ?? 0;
          if (days > 0) updatedDurationDays = days;
        } else if (newGoal.type === GoalType.ResponsibilitiesCompleted) {
          const days = newGoal.responsibilitiesCompletedTemplate?.timePeriodDays ?? 0;
          if (days > 0) updatedDurationDays = days;
        } else if (newGoal.type === GoalType.HeartCollection) {
          const days = newGoal.heartCollectionTemplate?.timePeriodDays ?? 0;
          if (days > 0) updatedDurationDays = days;
        }
        // HeartStreak goals never get an EndDate or DurationDays

        // Update existing goal - DON'T include image data in main update
        const goalUpdate = {
          title: generatedTitle,
          description: null,
          targetValue: newGoal.targetValue,
          rewardDescription: newGoal.reward,
          rewardImageUrl: editingGoal.rewardImageUrl, // Keep existing image URL
          dayPasses: newGoal.type === GoalType.StarCollection || newGoal.type === GoalType.ResponsibilitiesCompleted || newGoal.type === GoalType.HeartCollection ? 0 : (newGoal.dayPasses ?? 0), // Issue #488: Collection goals don't use squares
          isRecurring: newGoal.isRecurring || false, // Issue #431: Include recurring flag
          isNukeExempt: newGoal.isNukeExempt || false, // Issue #1310: Exempt from nuclear reset
          startDate: null,
          endDate: null, // Issue #1613: Server computes EndDate from durationDays; client does not send it
          durationDays: updatedDurationDays, // Issue #1613: Canonical duration; server uses this to set EndDate
          status: 1,
          type: newGoal.type // Issue #1758: Persist type change on edit
        };
        await goalsAPI.updateGoal(editingGoal.id, goalUpdate);

        // Handle image upload/removal for existing goals
        if (newGoal.rewardImageUrl !== editingGoal.rewardImageUrl) {
          if (newGoal.rewardImageUrl && !editingGoal.rewardImageUrl) {
            // Upload new image for goals that didn't have one before
            await goalsAPI.uploadGoalImage(editingGoal.id, newGoal.rewardImageUrl);
          } else if (!newGoal.rewardImageUrl && editingGoal.rewardImageUrl) {
            // Remove image if it was removed
            await goalsAPI.removeGoalImage(editingGoal.id);
          }
          // If editingGoal.rewardImageUrl exists and newGoal.rewardImageUrl exists, 
          // the image was already uploaded immediately in handleImageUpload
        }
        
        // Reload goals to get updated data with correct image URLs
        await loadGoals();
      } else {
        // Create new goals for multiple children
        const goalToSave = {
          childIds: selectedChildIds,
          title: generatedTitle,
          type: newGoal.type,
          targetValue: newGoal.targetValue,
          rewardDescription: newGoal.reward,
          dayPasses: newGoal.type === GoalType.StarCollection || newGoal.type === GoalType.ResponsibilitiesCompleted || newGoal.type === GoalType.HeartCollection ? 0 : (newGoal.dayPasses ?? 0), // Issue #488: Collection goals don't use squares
          isRecurring: newGoal.isRecurring || false, // Issue #431: Include recurring flag
          isNukeExempt: newGoal.isNukeExempt || false, // Issue #1310: Exempt from nuclear reset
          heartStreakTemplate: newGoal.heartStreakTemplate,
          starCollectionTemplate: newGoal.starCollectionTemplate,
          responsibilitiesCompletedTemplate: newGoal.responsibilitiesCompletedTemplate,
          heartCollectionTemplate: newGoal.heartCollectionTemplate
          // DON'T include rewardImageUrl in creation
        };
        
        const createdGoals = await goalsAPI.createGoal(goalToSave);
        
        // Handle both single goal and multiple goals response
        const goalsArray = Array.isArray(createdGoals) ? createdGoals : [createdGoals];
        
        // Upload image separately if provided for all created goals
        if (newGoal.rewardImageUrl) {
          await Promise.all(
            goalsArray.map(goal => 
              goalsAPI.uploadGoalImage(goal.id, newGoal.rewardImageUrl!)
            )
          );
        }
        
        // Show success message with child names
        const selectedChildren = children.filter(child => {
          const childId = child.childId || child.id;
          return childId !== undefined && selectedChildIds.includes(childId);
        });
        const childNames = selectedChildren.map(child => child.name).join(', ');
        showSnackbar(`${selectedChildIds.length} goals created for ${childNames}`, 'success');
        
        // Reload goals to get updated data with correct image URLs
        await loadGoals();
      }
      resetForm();
      setShowCreateGoal(false);
    } catch (error) {
      console.error(`Failed to ${editingGoal ? 'update' : 'create'} goal:`, error);
      showSnackbar(`Failed to ${editingGoal ? 'update' : 'create'} goal. Please try again.`, 'error');
    }
  };

  const handleDeleteGoal = (goalId: number) => {
    // Find the goal to delete and show confirmation modal (Issue #885)
    const goal = goals.find(g => g.id === goalId);
    if (goal) {
      setGoalToDelete(goal);
      setShowDeleteConfirmation(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!goalToDelete) return;

    try {
      await goalsAPI.deleteGoal(goalToDelete.id);
      setShowDeleteConfirmation(false);
      setGoalToDelete(null);
      await loadGoals(); // Re-fetch to update counts and pagination
    } catch (error) {
      console.error('Failed to delete goal:', error);
      showSnackbar('Failed to delete goal. Please try again.', 'error');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setGoalToDelete(null);
  };

  const handleDuplicateGoal = async (goalId: number) => {
    try {
      await goalsAPI.duplicateGoal(goalId);
      showSnackbar('Goal duplicated successfully!', 'success');
      await loadGoals(); // Re-fetch to show duplicated goal and update counts
    } catch (error) {
      console.error('Failed to duplicate goal:', error);
      showSnackbar('Failed to duplicate goal. Please try again.', 'error');
    }
  };

  const handleCelebrateGoalClick = (goal: Goal) => {
    const childName = getChildName(goal.childId);
    setCelebrationModal({ isOpen: true, goal, childName });
  };
  
  const handleGoalCelebrated = (celebratedGoal: Goal) => {
    // Update the editing goal if it's the same goal
    if (editingGoal && editingGoal.id === celebratedGoal.id) {
      setEditingGoal(celebratedGoal);
    }

    // Close modal and show success message
    setCelebrationModal({ isOpen: false, goal: null, childName: '' });
    showSnackbar('Goal celebrated successfully!', 'success');

    // Automatically switch to "Celebrated" filter to show the newly celebrated goal
    // This triggers the filter useEffect which resets page and reloads
    setGoalFilter('celebrated');
  };

  const handleCelebrateGoal = async (goalId: number) => {
    try {
      setCelebrationError(null);
      const celebratedGoal = await goalsAPI.celebrateGoal(goalId);

      // Update the editing goal if it's the same goal
      if (editingGoal && editingGoal.id === goalId) {
        setEditingGoal(celebratedGoal);
      }

      // Automatically switch to "Celebrated" filter - triggers filter useEffect
      setGoalFilter('celebrated');
    } catch (error) {
      console.error('Failed to celebrate goal:', error);
      setCelebrationError('Failed to mark goal as celebrated. Please try again.');
    }
  };

  const formatCelebrationDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getProgressPercentage = (goal: Goal): number => {
    const currentProgress = goal.currentProgress || 0;
    const targetValue = goal.targetValue || 1;

    if (targetValue === 0) return 0;

    const percentage = (currentProgress / targetValue) * 100;
    return Math.min(100, Math.round(percentage || 0));
  };

  // Issue #1309: Calculate days remaining display
  // Only show for goals with future EndDates (not past dates from seed data)
  const getDaysRemainingText = (goal: Goal): string | null => {
    if (goal.daysRemaining === null || goal.daysRemaining === undefined) {
      return null;
    }
    // Check if EndDate is in the past (daysRemaining=0 but EndDate < today)
    if (goal.endDate && !isGoalEndingToday(goal.endDate, familyTimezone)) {
      return null; // Don't show "Ends today" for already-ended goals
    }
    if (goal.daysRemaining === 0) {
      return 'Ends today';
    }
    if (goal.daysRemaining === 1) {
      return '1 day left';
    }
    return `${goal.daysRemaining} days left`;
  };

  // Issue #1309: Get urgency color for days remaining
  const getDaysRemainingColor = (daysRemaining: number | null | undefined): string => {
    if (daysRemaining === null || daysRemaining === undefined) {
      return theme.palette.success.main;
    }
    if (daysRemaining < 3) {
      return theme.palette.error.main;
    }
    if (daysRemaining <= 7) {
      return theme.palette.warning.main;
    }
    return theme.palette.success.main;
  };

  // Issue #1309: Calculate day passes display (HeartStreak and HeartCollection)
  const getDayPassesText = (goal: Goal): string | null => {
    if ((goal.type !== GoalType.HeartStreak && goal.type !== GoalType.HeartCollection) || !goal.dayPasses || goal.dayPasses <= 0) {
      return null;
    }
    const used = goal.dayPassesUsed ?? 0;
    const remaining = goal.dayPasses - used;
    const passWord = remaining === 1 ? 'day pass' : 'day passes';
    return `${remaining}/${goal.dayPasses} ${passWord} left`;
  };

  // Issue #1421: Format lifecycle display for goal cards
  const getLifecycleText = (goal: Goal): string => {
    const formatDate = (dateStr: string | undefined | null): string | null => {
      if (!dateStr) return null;

      const datePart = dateStr.split('T')[0];
      const parts = datePart.split('-').map(Number);

      if (parts.length < 3 || parts.some(isNaN)) return null;

      const [year, month, day] = parts;
      const today = new Date();
      const sameYear = year === today.getFullYear();

      const monthDate = new Date(year, month - 1, day);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });

      if (sameYear) {
        return `${monthName} ${day}`;
      }
      return `${monthName} ${day}, ${year}`;
    };

    const lifecycleParts: string[] = [];

    // Start date (or placeholder)
    const startFormatted = formatDate(goal.startDate);
    lifecycleParts.push(`Started ${startFormatted || '?'}`);

    // Completed or Failed
    if (goal.completedAt) {
      const completedFormatted = formatDate(goal.completedAt);
      if (completedFormatted) {
        lifecycleParts.push(`Completed ${completedFormatted}`);
      }
    } else if (goal.failedAt) {
      const failedFormatted = formatDate(goal.failedAt);
      if (failedFormatted) {
        lifecycleParts.push(`Failed ${failedFormatted}`);
      }
    }

    // Celebrated
    if (goal.celebratedAt) {
      const celebratedFormatted = formatDate(goal.celebratedAt);
      if (celebratedFormatted) {
        lifecycleParts.push(`Celebrated ${celebratedFormatted}`);
      }
    }

    return lifecycleParts.join(' → ');
  };

  const getChildName = (childId: number): string => {
    const child = children.find(c => (c.childId || c.id) === childId);
    return child?.name || 'Unknown Child';
  };

  // Enhanced filtering helper functions (Issue #883)
  const getChildDropdownOptions = (): Array<{ value: number | null; label: string }> => {
    const options: Array<{ value: number | null; label: string }> = [{ value: null, label: 'All Children' }];
    children.forEach(child => {
      const childId = child.childId || child.id;
      if (childId) {
        options.push({ value: childId, label: child.name });
      }
    });
    return options;
  };

  const getGoalTypeDisplay = (type: GoalType): string => {
    switch (type) {
      case GoalType.HeartStreak:
        return 'Heart Streak';
      case GoalType.StarCollection:
        return 'Star Collection';
      case GoalType.ResponsibilitiesCompleted:
        return 'Responsibilities Completed';
      case GoalType.HeartCollection:
        return 'Heart Collection';
      default:
        return 'Unknown';
    }
  };

  const getActiveFilterChips = () => {
    const chips = [];

    // Child filter chip
    if (childFilter !== null) {
      const childName = getChildName(childFilter);
      chips.push({
        id: 'child',
        label: `Child: ${childName}`,
        onRemove: () => setChildFilter(null)
      });
    }

    // Goal type filter chips (only if not all are selected)
    const allGoalTypes = [GoalType.HeartStreak, GoalType.StarCollection, GoalType.ResponsibilitiesCompleted, GoalType.HeartCollection];
    if (goalTypeFilters.length !== allGoalTypes.length) {
      const typeNames = goalTypeFilters.map(type => getGoalTypeDisplay(type)).join(', ');
      chips.push({
        id: 'goal-types',
        label: `Goal Types: ${typeNames}`,
        onRemove: () => setGoalTypeFilters(allGoalTypes)
      });
    }

    // Recurring filter chip
    if (recurringFilter) {
      chips.push({
        id: 'recurring',
        label: 'Recurring',
        onRemove: () => setRecurringFilter(false)
      });
    }

    // Status filter chip (if not showing open goals)
    if (goalFilter !== 'open') {
      const statusLabels: Record<string, string> = {
        'completed': 'Completed',
        'celebrated': 'Celebrated',
        'failed': 'Failed'
      };
      chips.push({
        id: 'status',
        label: statusLabels[goalFilter],
        onRemove: () => setGoalFilter('open')
      });
    }

    return chips;
  };

  // Helper functions for goal state checking
  const isOpenGoal = (goal: Goal): boolean => goal.status === GoalStatus.Active;
  const isCompletedGoal = (goal: Goal): boolean => goal.status === GoalStatus.Completed;
  const isCelebratedGoal = (goal: Goal): boolean => goal.status === GoalStatus.Celebrated;

  // Server-side pagination: goals are already filtered and sorted by the API (Issue #1426)
  const filteredGoals = goals;
  const goalCounts = statusCounts;
  const activeFilterChips = getActiveFilterChips();

  // Calculate filter count for display
  const totalGoalsCount = statusCounts.open + statusCounts.completed + statusCounts.celebrated + statusCounts.failed;
  const filteredGoalsCount = totalCount;
  const isFiltered = childFilter !== null || goalTypeFilters.length !== 3 || recurringFilter || goalFilter !== 'open';

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading goals...</Typography>
      </Paper>
    );
  }

  return (
    <Box data-testid="goals-manager-container">
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
            {/* lucide-react icons use style, not sx — they don't accept an sx prop */}
            <Target style={{ marginRight: 8, width: 20, height: 20 }} />
            Goals Management
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              // Reset form for new goal creation
              setEditingGoal(null);
              setSelectedChildIds([]);
              setNewGoal({
                childId: 0,
                title: '',
                type: GoalType.HeartStreak,
                targetValue: 5,
                reward: '',
                dayPasses: 1, // HeartStreak goals still use squares
                heartStreakTemplate: {
                  consecutiveDays: 5,
                  dayPasses: 1
                },
                starCollectionTemplate: {
                  totalStars: 10,
                  timePeriodDays: 0
                },
                heartCollectionTemplate: {
                  totalHearts: 10,
                  timePeriodDays: 0
                }
              });
              setShowCreateGoal(true);
            }}
            startIcon={<Plus size={16} />}
            size="small"
          >
            Add Goal
          </Button>
        </Box>

        {/* Enhanced Filter Section (Issue #883) */}
        <Box
          data-testid="goals-filter-container"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'stretch', md: 'space-between' },
            gap: 2,
            mb: 3
          }}
        >
          {/* Status Filter Buttons - Left Side */}
          <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <ButtonGroup
              variant="outlined"
              size="small"
              sx={{
                '& .MuiButton-root': {
                  minWidth: '100px',
                  // fontSize matches the button variant's own baseline; not a Typography override
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary
                },
                '& .MuiButton-root.selected': {
                  backgroundColor: theme.palette.info.main,
                  // nalu-v1's info.contrastText is explicitly black (white fails WCAG
                  // contrast on that palette's sea-green) — other themes keep white (issue #1900)
                  color: isNaluV1Theme ? theme.palette.info.contrastText : 'white',
                  borderColor: theme.palette.info.main,
                  '&:hover': {
                    backgroundColor: theme.palette.info.dark,
                    borderColor: theme.palette.info.dark
                  }
                },
                '& .MuiButton-root:not(.selected):hover': {
                  backgroundColor: theme.palette.grey[100],
                  borderColor: theme.palette.divider
                }
              }}
            >
              <Button
                onClick={() => setGoalFilter('open')}
                className={goalFilter === 'open' ? 'selected' : ''}
                data-testid="goal-filter-open"
              >
                Open ({goalCounts.open})
              </Button>
              <Button
                onClick={() => setGoalFilter('completed')}
                className={goalFilter === 'completed' ? 'selected' : ''}
                data-testid="goal-filter-completed"
              >
                Completed ({goalCounts.completed})
              </Button>
              <Button
                onClick={() => setGoalFilter('celebrated')}
                className={goalFilter === 'celebrated' ? 'selected' : ''}
                data-testid="goal-filter-celebrated"
              >
                Celebrated ({goalCounts.celebrated})
              </Button>
              <Button
                onClick={() => setGoalFilter('failed')}
                className={goalFilter === 'failed' ? 'selected' : ''}
                data-testid="goal-filter-failed"
              >
                Failed ({goalCounts.failed})
              </Button>
            </ButtonGroup>
          </Box>

          {/* New Filters - Right Side */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            {/* Child Filter Dropdown — hidden when scoped to a single child (e.g., child profile page) */}
            {children.length !== 1 && <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by child</InputLabel>
              <Select
                value={childFilter || ''}
                label="Filter by child"
                onChange={(e) => {
                  const stringValue = String(e.target.value);
                  const value = stringValue === '' ? null : Number(stringValue);
                  setChildFilter(value);
                }}
                aria-label="Filter by child"
              >
                {getChildDropdownOptions().map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value || ''}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>}

            {/* Goal Type Filter Checkboxes */}
            {[
              { type: GoalType.HeartStreak, label: 'Heart Streak' },
              { type: GoalType.StarCollection, label: 'Star Collection' },
              { type: GoalType.ResponsibilitiesCompleted, label: 'Responsibilities Completed' },
              { type: GoalType.HeartCollection, label: 'Heart Collection' }
            ].map(({ type, label }) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={goalTypeFilters.includes(type)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setGoalTypeFilters([...goalTypeFilters, type]);
                      } else {
                        setGoalTypeFilters(goalTypeFilters.filter(t => t !== type));
                      }
                    }}
                    size="small"
                    color="primary"
                  />
                }
                label={label}
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    // fontSize matches body2/button, but FormControlLabel has no Typography variant to swap to
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}

            {/* Recurring Filter Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={recurringFilter}
                  onChange={(e) => setRecurringFilter(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Show only recurring goals"
              sx={{
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  // fontSize matches body2/button, but FormControlLabel has no Typography variant to swap to
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Box>

        {/* Filter Count and Active Filter Chips */}
        {(isFiltered || activeFilterChips.length > 0) && (
          <Box sx={{ mb: 2 }}>
            {/* Filter Count */}
            {isFiltered && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Showing {filteredGoalsCount} of {totalGoalsCount} goals
              </Typography>
            )}

            {/* Active Filter Chips */}
            {activeFilterChips.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {activeFilterChips.map((chip) => (
                  <Chip
                    key={chip.id}
                    label={chip.label}
                    size="small"
                    onDelete={chip.onRemove}
                    color="primary"
                    variant="outlined"
                    data-testid={`filter-chip-${chip.id}`}
                    deleteIcon={
                      <Box component="span" data-testid={`filter-chip-${chip.id}-remove`} sx={{ cursor: 'pointer' }}>
                        ×
                      </Box>
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

          {filteredGoals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Target style={{ width: 48, height: 48, color: neutralGray[400], marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
              <Typography color="text.secondary" gutterBottom>
                {goals.length === 0 ? 'No goals created yet.' :
                 goalFilter === 'open' ? 'No open goals.' :
                 goalFilter === 'completed' ? 'No completed goals.' :
                 goalFilter === 'celebrated' ? 'No celebrated goals.' :
                 goalFilter === 'failed' ? 'No failed goals.' :
                 'No goals found.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {goals.length === 0 ? 'Create goals to motivate your children!' :
                 goalFilter === 'open' ? 'All goals are completed or celebrated!' :
                 goalFilter === 'completed' ? 'No goals waiting for celebration!' :
                 goalFilter === 'celebrated' ? 'No goals have been celebrated yet!' :
                 goalFilter === 'failed' ? 'No goals have failed - great job!' :
                 'Try changing the filter above.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredGoals.map((goal) => (
                <Paper key={goal.id} variant="outlined" sx={{ p: 2 }} data-testid={`goal-card-${goal.id}`}>
                  {/* Mobile Layout: Vertical Stacking */}
                  <Box sx={{ 
                    display: { xs: 'block', md: 'flex' },
                    alignItems: { md: 'flex-start' },
                    justifyContent: { md: 'space-between' }
                  }}>
                    
                    {/* Title Row - Full width on mobile */}
                    <Box sx={{ 
                      mb: { xs: 2, md: 0 },
                      flex: { md: 1 }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: { xs: 2, md: 2 },
                        flexWrap: { xs: 'nowrap', md: 'nowrap' }
                      }}>
                        {/* Title and Recurring Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography variant="h6" component="h3" sx={{
                            flexGrow: { xs: 1, md: 0 },
                            mr: { xs: 1, md: 0 }
                          }}>
                            {goal.title}
                          </Typography>
                          {goal.isRecurring && (
                            <Tooltip title="Recurring goal">
                              <Repeat
                                size={16}
                                style={{
                                  color: theme.palette.info.main,
                                  marginLeft: 4,
                                  pointerEvents: 'none',
                                  cursor: 'default'
                                }}
                              />
                            </Tooltip>
                          )}
                          {/* Nuclear reset exemption indicator (Issue #1310) */}
                          {goal.isNukeExempt && (
                            <Tooltip title="Protected from nuclear reset">
                              {/* Icon glyph sizing via fontSize, not a Typography text override */}
                              <ShieldIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </Box>

                        {/* Desktop Action Buttons - next to title */}
                        <Box sx={{
                          display: { xs: 'none', md: 'flex' },
                          gap: 0.5,
                          flexShrink: 0
                        }}>
                          {isCompletedGoal(goal) && (
                            <IconButton
                              onClick={() => handleCelebrateGoalClick(goal)}
                              data-testid={`celebrate-goal-button-${goal.id}`}
                              sx={{
                                backgroundColor: celebrationGold,
                                color: theme.palette.text.primary,
                                '&:hover': {
                                  backgroundColor: celebrationGoldHover,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 4px 12px ${alpha(celebrationGold, 0.4)}`
                                },
                                transition: 'all 0.2s ease',
                                width: '32px',
                                height: '32px'
                              }}
                              size="small"
                              title="Celebrate this goal"
                              aria-label={`Celebrate goal for ${getChildName(goal.childId)}`}
                            >
                              <PartyPopper size={14} />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => handleEditGoal(goal)}
                            color="primary"
                            size="small"
                            sx={{ width: '32px', height: '32px' }}
                            title="Edit goal"
                          >
                            <Edit size={14} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDuplicateGoal(goal.id)}
                            color="secondary"
                            size="small"
                            sx={{ width: '32px', height: '32px' }}
                            title="Duplicate goal"
                          >
                            <Copy size={14} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteGoal(goal.id)}
                            color="error"
                            size="small"
                            sx={{ width: '32px', height: '32px' }}
                            title="Delete goal"
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Chips + Buttons Row - Only on mobile */}
                      <Box sx={{ 
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2
                      }}>
                        {/* Chips on left */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, mr: 2 }}>
                          <Chip 
                            label={getChildName(goal.childId)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip 
                            label={getGoalTypeDisplay(goal.type)}
                            size="small"
                            variant="outlined"
                          />
                          {isCompletedGoal(goal) && (
                            <Chip
                              label="✓ Completed"
                              size="small"
                              color="success"
                              variant="filled"
                            />
                          )}
                          {isCelebratedGoal(goal) && (
                            <Chip
                              label="🎉 Celebrated"
                              size="small"
                              color="secondary"
                              variant="filled"
                            />
                          )}
                        </Box>

                        {/* Action buttons on right - Mobile */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* Celebrate Button - only show for completed but not celebrated goals */}
                          {isCompletedGoal(goal) && (
                            <IconButton
                              onClick={() => handleCelebrateGoalClick(goal)}
                              data-testid={`celebrate-goal-button-${goal.id}`}
                              sx={{
                                backgroundColor: celebrationGold,
                                color: theme.palette.text.primary,
                                '&:hover': {
                                  backgroundColor: celebrationGoldHover,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 4px 12px ${alpha(celebrationGold, 0.4)}`
                                },
                                transition: 'all 0.2s ease',
                                width: '48px',
                                height: '48px'
                              }}
                              size="small"
                              title="Celebrate this goal"
                              aria-label={`Celebrate goal for ${getChildName(goal.childId)}`}
                            >
                              <PartyPopper size={16} />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => handleEditGoal(goal)}
                            color="primary"
                            size="small"
                            sx={{ width: '48px', height: '48px' }}
                            title="Edit goal"
                          >
                            <Edit size={16} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDuplicateGoal(goal.id)}
                            color="secondary"
                            size="small"
                            sx={{ width: '48px', height: '48px' }}
                            title="Duplicate goal"
                          >
                            <Copy size={16} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteGoal(goal.id)}
                            color="error"
                            size="small"
                            sx={{ width: '48px', height: '48px' }}
                            title="Delete goal"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Desktop Chips Row - Only on desktop */}
                      <Box sx={{ 
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center', 
                        gap: 1, 
                        mb: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Chip 
                          label={getChildName(goal.childId)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip 
                          label={getGoalTypeDisplay(goal.type)}
                          size="small"
                          variant="outlined"
                        />
                        {isCompletedGoal(goal) && (
                          <Chip
                            label="✓ Completed"
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        )}
                        {isCelebratedGoal(goal) && (
                          <Chip
                            label="🎉 Celebrated"
                            size="small"
                            color="secondary"
                            variant="filled"
                          />
                        )}
                      </Box>
                      
                      {/* Progress + Reward Row - Full width */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            Progress: {goal.currentProgress || 0} / {goal.targetValue || 0}
                          </Typography>
                          <Typography variant="body2">
                            {getProgressPercentage(goal)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getProgressPercentage(goal)}
                          sx={{ height: 8, borderRadius: isNaluV1Theme ? '4px' : 4 }}
                        />
                      </Box>

                      {/* Issue #1309: Days Remaining and Day Passes */}
                      {isOpenGoal(goal) && (getDaysRemainingText(goal) || getDayPassesText(goal)) && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          {getDaysRemainingText(goal) && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: getDaysRemainingColor(goal.daysRemaining)
                              }}
                            >
                              {getDaysRemainingText(goal)}
                            </Typography>
                          )}
                          {!getDaysRemainingText(goal) && <Box />}
                          {getDayPassesText(goal) && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: 'text.secondary'
                              }}
                            >
                              {getDayPassesText(goal)}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {(goal.rewardDescription || goal.reward || goal.rewardImageUrl) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          {goal.rewardImageUrl && (
                            <Avatar
                              data-testid={`goal-${goal.id}-reward-image`}
                              src={getFullApiUrl(goal.rewardImageUrl)}
                              alt="Reward image"
                              sx={{
                                width: 24,
                                height: 24,
                                border: `1px solid ${theme.palette.grey[300]}`
                              }}
                            />
                          )}
                          {(goal.rewardDescription || goal.reward) && (
                            <Typography variant="body2" color="success.main">
                              🎁 Reward: {goal.rewardDescription || goal.reward}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Issue #1421: Lifecycle Display Row */}
                      <Box
                        data-testid={`goal-lifecycle-row-${goal.id}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 1
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}
                        >
                          {getLifecycleText(goal)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {/* Server-side pagination controls (Issue #1426) */}
          {totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              data-testid="goals-pagination"
              aria-label="Goals pagination"
            />
          )}
      </Paper>

      {/* Create Goal Modal */}
    <Dialog 
      open={showCreateGoal} 
      onClose={() => {}} // Prevent closing on backdrop click or escape key
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>{editingGoal ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
      <DialogContent>
          
        <Box sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Select Children *
              </Typography>
              <Box 
                data-testid="child-selection-buttons"
                role="group"
                aria-label="Select children for goal"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 1
                }}
              >
                {children.map((child) => {
                  const childId = child.childId || child.id;
                  if (childId === undefined) return null;
                  const isSelected = selectedChildIds.includes(childId);
                  
                  return (
                    <Button
                      key={childId}
                      data-testid={`child-button-${childId}`}
                      variant={isSelected ? 'contained' : 'outlined'}
                      disabled={!!editingGoal} // Disable when editing existing goal
                      onClick={() => {
                        if (!editingGoal) {
                          setSelectedChildIds(prev => 
                            isSelected 
                              ? prev.filter(id => id !== childId)
                              : [...prev, childId]
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !editingGoal) {
                          e.preventDefault();
                          setSelectedChildIds(prev => 
                            isSelected 
                              ? prev.filter(id => id !== childId)
                              : [...prev, childId]
                          );
                        }
                      }}
                      aria-pressed={isSelected}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1
                      }}
                    >
                      {child.name}
                    </Button>
                  );
                })}
              </Box>
              {selectedChildIds.length === 0 && !editingGoal && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Please select at least one child
                </Typography>
              )}
            </Box>
            
{/* Goal Title field removed per specification */}
            
            <FormControl fullWidth>
              <InputLabel>Goal Type</InputLabel>
              <Select
                value={newGoal.type}
                onChange={(e) => {
                  const newType = e.target.value as GoalType;
                  let newTargetValue = newGoal.targetValue;
                  
                  // Update targetValue to match the template when switching goal types
                  if (newType === GoalType.HeartStreak) {
                    newTargetValue = newGoal.heartStreakTemplate?.consecutiveDays || 5;
                  } else if (newType === GoalType.StarCollection) {
                    newTargetValue = newGoal.starCollectionTemplate?.totalStars || 10;
                  } else if (newType === GoalType.ResponsibilitiesCompleted) {
                    newTargetValue = newGoal.responsibilitiesCompletedTemplate?.totalLists || 5;
                  } else if (newType === GoalType.HeartCollection) {
                    newTargetValue = newGoal.heartCollectionTemplate?.totalHearts || 10;
                  }
                  
                  setNewGoal({ ...newGoal, type: newType, targetValue: newTargetValue });
                }}
                label="Goal Type"
              >
                <MenuItem value={GoalType.HeartStreak}>Heart Streak</MenuItem>
                <MenuItem value={GoalType.StarCollection}>Star Collection</MenuItem>
                <MenuItem value={GoalType.ResponsibilitiesCompleted}>Responsibilities Completed</MenuItem>
                <MenuItem value={GoalType.HeartCollection}>Heart Collection</MenuItem>
              </Select>
            </FormControl>
            
            {/* Dynamic Goal Type Fields */}
            <Box data-testid="dynamic-fields">
              <Fade in={true} timeout={300}>
                <Box>
                  {newGoal.type === GoalType.HeartStreak && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" color="primary">
                        Heart Streak Settings
                      </Typography>
                      <TextField
                        label="Consecutive Days (2-30)"
                        type="number"
                        inputProps={{ min: 2, max: 30 }}
                        value={newGoal.heartStreakTemplate?.consecutiveDays ?? 5}
                        onChange={(e) => {
                          const consecutiveDays = parseInt(e.target.value) || 2;
                          setNewGoal({ 
                            ...newGoal,
                            targetValue: consecutiveDays, // Update targetValue to match consecutiveDays
                            heartStreakTemplate: {
                              ...newGoal.heartStreakTemplate!,
                              consecutiveDays: consecutiveDays
                            }
                          });
                        }}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Day Passes (0+)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={newGoal.dayPasses ?? 0}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                          const clampedValue = isNaN(value) ? 0 : Math.max(value, 0);
                          setNewGoal({ 
                            ...newGoal, 
                            dayPasses: clampedValue
                          });
                        }}
                        required
                        fullWidth
                      />
                    </Stack>
                  )}
                  
                  {newGoal.type === GoalType.StarCollection && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" color="primary">
                        Star Collection Settings
                      </Typography>
                      <TextField
                        label="Total Stars to Collect (1+)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={newGoal.starCollectionTemplate?.totalStars ?? 10}
                        onChange={(e) => {
                          const totalStars = parseInt(e.target.value) || 1;
                          setNewGoal({ 
                            ...newGoal,
                            targetValue: totalStars, // Update targetValue to match totalStars
                            starCollectionTemplate: {
                              ...newGoal.starCollectionTemplate!,
                              totalStars: totalStars
                            }
                          });
                        }}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Time Period in Days"
                        type="number"
                        placeholder="No limit"
                        value={newGoal.starCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.starCollectionTemplate?.timePeriodDays ?? '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = parseInt(value);
                          
                          // Auto-clear to empty when user enters 0
                          if (numValue === 0) {
                            setNewGoal({ 
                              ...newGoal, 
                              starCollectionTemplate: {
                                ...newGoal.starCollectionTemplate!,
                                timePeriodDays: 0
                              }
                            });
                            // Clear the input field
                            e.target.value = '';
                            return;
                          }
                          
                          setNewGoal({ 
                            ...newGoal, 
                            starCollectionTemplate: {
                              ...newGoal.starCollectionTemplate!,
                              timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                            }
                          });
                        }}
                        fullWidth
                      />
                    </Stack>
                  )}
                  
                  {newGoal.type === GoalType.ResponsibilitiesCompleted && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" color="primary">
                        Responsibilities Completed Settings
                      </Typography>
                      <TextField
                        label="Total Lists to Complete (1+)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={newGoal.responsibilitiesCompletedTemplate?.totalLists ?? 5}
                        onChange={(e) => {
                          const totalLists = parseInt(e.target.value) || 1;
                          setNewGoal({
                            ...newGoal,
                            targetValue: totalLists,
                            responsibilitiesCompletedTemplate: {
                              ...newGoal.responsibilitiesCompletedTemplate!,
                              totalLists: totalLists
                            }
                          });
                        }}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Time Period in Days"
                        type="number"
                        placeholder="No limit"
                        value={newGoal.responsibilitiesCompletedTemplate?.timePeriodDays === 0 ? '' : (newGoal.responsibilitiesCompletedTemplate?.timePeriodDays ?? '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = parseInt(value);
                          if (numValue === 0) {
                            setNewGoal({
                              ...newGoal,
                              responsibilitiesCompletedTemplate: {
                                ...newGoal.responsibilitiesCompletedTemplate!,
                                timePeriodDays: 0
                              }
                            });
                            e.target.value = '';
                            return;
                          }
                          setNewGoal({
                            ...newGoal,
                            responsibilitiesCompletedTemplate: {
                              ...newGoal.responsibilitiesCompletedTemplate!,
                              timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                            }
                          });
                        }}
                        fullWidth
                      />
                    </Stack>
                  )}

                  {newGoal.type === GoalType.HeartCollection && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" color="primary">
                        Heart Collection Settings
                      </Typography>
                      <TextField
                        label="Total Hearts to Collect (1+)"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={newGoal.heartCollectionTemplate?.totalHearts ?? 10}
                        onChange={(e) => {
                          const totalHearts = parseInt(e.target.value) || 1;
                          setNewGoal({ 
                            ...newGoal,
                            targetValue: totalHearts, // Update targetValue to match totalHearts
                            heartCollectionTemplate: {
                              ...newGoal.heartCollectionTemplate!,
                              totalHearts: totalHearts
                            }
                          });
                        }}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Time Period in Days"
                        type="number"
                        placeholder="No limit"
                        value={newGoal.heartCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.heartCollectionTemplate?.timePeriodDays ?? '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = parseInt(value);
                          
                          // Auto-clear to empty when user enters 0
                          if (numValue === 0) {
                            setNewGoal({ 
                              ...newGoal, 
                              heartCollectionTemplate: {
                                ...newGoal.heartCollectionTemplate!,
                                timePeriodDays: 0
                              }
                            });
                            // Clear the input field
                            e.target.value = '';
                            return;
                          }
                          
                          setNewGoal({ 
                            ...newGoal, 
                            heartCollectionTemplate: {
                              ...newGoal.heartCollectionTemplate!,
                              timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                            }
                          });
                        }}
                        fullWidth
                      />
                    </Stack>
                  )}
                </Box>
              </Fade>
            </Box>
            
            <Box>
              <TextField
                label="Reward (Optional)"
                value={newGoal.reward}
                onChange={(e) => setNewGoal({ ...newGoal, reward: e.target.value })}
                placeholder="e.g., 'Extra 30 minutes screen time'"
                fullWidth
              />
              
              {/* Reward Image Upload Section */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Reward Image:
                </Typography>
                
                {newGoal.rewardImageUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      data-testid="reward-image-thumbnail"
                      src={newGoal.rewardImageUrl?.startsWith('data:') ? newGoal.rewardImageUrl : getFullApiUrl(newGoal.rewardImageUrl)}
                      alt="Reward image"
                      sx={{
                        width: 40,
                        height: 40,
                        cursor: 'pointer',
                        border: `2px solid ${theme.palette.grey[300]}`
                      }}
                      onClick={() => setShowImageUpload(true)}
                    />
                    <Button
                      data-testid="remove-reward-image-button"
                      size="small"
                      color="error"
                      onClick={() => setShowRemoveImageConfirmation(true)}
                    >
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Button
                    data-testid="reward-image-upload-button"
                    variant="outlined"
                    size="small"
                    startIcon={<Camera size={16} />}
                    onClick={() => setShowImageUpload(true)}
                  >
                    Add Image
                  </Button>
                )}
              </Box>
            </Box>
            
            {/* Recurring Goal Checkbox */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newGoal.isRecurring || false}
                    onChange={(e) => setNewGoal({ ...newGoal, isRecurring: e.target.checked })}
                    color="primary"
                  />
                }
                label="Set as recurring"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                Goal will automatically recreate when completed or failed
              </Typography>
            </Box>

            {/* Nuclear Reset Exemption Checkbox (Issue #1310) */}
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newGoal.isNukeExempt || false}
                    onChange={(e) => setNewGoal({ ...newGoal, isNukeExempt: e.target.checked })}
                    color="warning"
                  />
                }
                label="Exempt from nuclear reset"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                This goal will not be reset when a parent uses the nuclear reset option
              </Typography>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      {celebrationError && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography color="error" variant="body2">
            {celebrationError}
          </Typography>
        </Box>
      )}
      <DialogActions>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateGoal(false);
            setCelebrationError(null);
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        
        {/* Celebration Button - only show for completed goals when editing */}
        {editingGoal && (editingGoal.status === GoalStatus.Completed || editingGoal.status === GoalStatus.Celebrated) && (
          editingGoal.status === GoalStatus.Celebrated ? (
            <Button variant="outlined" disabled>
              Celebrated on {formatCelebrationDate(editingGoal.celebratedAt!)}
            </Button>
          ) : (
            <Button 
              variant="contained"
              color="primary"
              onClick={() => handleCelebrateGoal(editingGoal.id)}
            >
              Mark as Celebrated
            </Button>
          )
        )}
        
        <Button 
          variant="contained"
          onClick={handleSaveGoal}
        >
          {editingGoal ? 'Update Goal' : 'Create Goal'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Reward Image Upload Modal */}
    <AvatarUploadModal
      data-testid="avatar-upload-modal"
      data-entity-type="goal"
      isOpen={showImageUpload}
      onSave={handleImageUpload}
      onClose={() => setShowImageUpload(false)}
      currentAvatarUrl={newGoal.rewardImageUrl ? getFullApiUrl(newGoal.rewardImageUrl) : undefined}
      entityType="goal"
      entityId={editingGoal?.id}
    />

    {/* Remove Image Confirmation Dialog */}
    <Dialog
      open={showRemoveImageConfirmation}
      onClose={() => setShowRemoveImageConfirmation(false)}
    >
      <DialogTitle>Remove Reward Image</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to remove the reward image?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowRemoveImageConfirmation(false)}>
          Cancel
        </Button>
        <Button onClick={handleRemoveImage} color="error">
          Yes, Remove It
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Goal Confirmation Dialog (Issue #885) */}
    <Dialog
      open={showDeleteConfirmation}
      onClose={handleCancelDelete}
    >
      <DialogTitle>Delete Goal</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {goalToDelete && `Are you sure you want to delete the goal '${goalToDelete.title}' for ${getChildName(goalToDelete.childId)}?`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelDelete}>Cancel</Button>
        <Button onClick={handleConfirmDelete} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for feedback */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={3000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    
    {/* Celebration Modal */}
    <CelebrationModal
      isOpen={celebrationModal.isOpen}
      goal={celebrationModal.goal}
      childName={celebrationModal.childName}
      onClose={() => setCelebrationModal({ isOpen: false, goal: null, childName: '' })}
      onCelebrated={handleGoalCelebrated}
    />
    </Box>
  );
};

export default GoalsManager;